import { useRequestErrorToast } from '@onefootprint/hooks';
import type { ApiNodeDataConditional, GraphOperationUpdateNode, PublicBranchRule, PublicConditionalBranch } from '@onefootprint/request-types/dashboard';
import { Divider, Form, Shimmer } from '@onefootprint/ui';
import { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '../../hooks/use-dirty';
import { useGetNodeConfigContext } from '../../hooks/use-get-node-config-context';
import { useGetTraceContext } from '../../hooks/use-get-trace-context';
import useStore from '../../hooks/use-store';
import type { ApplyEditMutation } from '../base-node';
import BaseBranch from './components/base-branch';
import Branch from './components/branch';
import { MonacoProvider } from './hooks/use-monaco/use-monaco-context';
export type Rule = {
  ruleId: string;
  ruleExpression: string;
  isPendingDeletion?: boolean;
};
export type BranchInfo = {
  name: string;
  successor: string;
};
export type ConditionalBranch = {
  rules: Rule[];
  branch: BranchInfo;
};
export type FormData = {
  branches: ConditionalBranch[];
  defaultBranch: BranchInfo;
  name?: string;
};
export type ConditionalFormProps = {
  data: ApiNodeDataConditional;
  nodeId: string;
  mutation: ApplyEditMutation;
};
const ConditionalForm = ({
  data,
  nodeId,
  mutation
}: ConditionalFormProps) => {
  const {
    t
  } = useTranslation('playbook-builder');
  const showRequestErrorToast = useRequestErrorToast();
  const {
    data: {
      canEdit
    }
  } = useStore();
  const config = useGetNodeConfigContext(nodeId, 'conditional');
  const trace = useGetTraceContext(nodeId, 'conditional');
  const form = useForm<FormData>({
    defaultValues: {
      branches: data.conditionalBranches?.map(cb => ({
        rules: cb.rules?.map(r => ({
          ruleId: r.ruleId,
          ruleExpression: r.ruleExpression || ''
        })) || [],
        branch: {
          name: cb.branch.name || '',
          successor: cb.branch.successor
        }
      })) || [],
      defaultBranch: {
        name: data.defaultBranch?.name || 'Pass',
        successor: data.defaultBranch?.successor
      },
      name: data.name
    }
  });
  const {
    fields
  } = useFieldArray({
    control: form.control,
    name: 'branches'
  });
  const {
    handleSubmit,
    formState,
    clearErrors
  } = form;
  const {
    toggle
  } = useDirty();

  // note: we need to listen to formState vs. the individual fields
  // see https://react-hook-form.com/docs/useform/formstate
  useEffect(() => {
    toggle('rules-form', !!formState.isDirty);
  }, [formState.isDirty]);

  // on initial open, there are no errors - impossible to submit / close with errors
  useEffect(() => {
    clearErrors();
  }, []);
  const onSubmit = async (data_0: FormData) => {
    // react-hook-form quirk; isValid is NOT changed by setErrors calls
    // so we need to check the errors object directly
    // https://react-hook-form.com/docs/useform/formstate
    const hasErrors = Object.keys(formState.errors).length > 0;
    if (hasErrors) {
      showRequestErrorToast(new Error(t($ => $.rulesNode.form.validationSummaryError)));
      return;
    }
    try {
      const operation: GraphOperationUpdateNode = {
        kind: 'update_node',
        nodeId,
        nodeData: {
          kind: 'conditional',
          conditionalBranches: data_0.branches.map((branch): PublicConditionalBranch => ({
            rules: branch.rules.filter(rule => !rule.isPendingDeletion).map((rule_0): PublicBranchRule => ({
              ruleId: rule_0.ruleId,
              expression: rule_0.ruleExpression
            })),
            branch: {
              name: branch.branch.name,
              successor: branch.branch.successor ?? ''
            }
          })),
          defaultBranch: {
            name: data_0.defaultBranch.name,
            successor: data_0.defaultBranch.successor ?? ''
          },
          name: !data_0.name || data_0.name === 'Rules' ? undefined : data_0.name
        }
      };
      await mutation.mutateAsync(operation);
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };
  if (!config) {
    return <div className="flex flex-col gap-8">
        <Shimmer width="100%" height="145px" />
        <Shimmer width="100%" height="145px" />
        <Shimmer width="100%" height="32px" />
      </div>;
  }
  return <MonacoProvider languageDefinition={config.languageDefinition}>
      <FormProvider {...form}>
        <form id="rules-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 w-full md:flex-row md:justify-between md:items-center md:gap-0">
            <div className="text-label-2">{t($_0 => $_0.rulesNode.nodeName)}</div>
            <div className="w-full md:w-[50%]">
              <Form.Input<FormData> name="name" disabled={!canEdit} className="md:w-[50%]" placeholder={t($_1 => $_1.rulesNode.namePlaceholder)} size="compact" />
            </div>
          </div>
          <Divider />
          <div className="flex flex-col pb-4">
            {fields.map((field, index) => <div key={field.id}>
                <Branch index={index} trace={trace} canEdit={canEdit} languageName={config.languageDefinition.languageName} nodeId={nodeId} />
                <div className="pl-6 flex gap-3 items-center h-8">
                  <div className="w-[1px] h-full bg-gray-100" />
                  <div className="text-body-2 text-tertiary">{t($_2 => $_2.rulesNode.branch.otherwiseLabel)}</div>
                </div>
              </div>)}
            <BaseBranch trace={trace} canEdit={canEdit} branchIndex="default" />
          </div>
        </form>
      </FormProvider>
    </MonacoProvider>;
};
export default ConditionalForm;